import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  BookOpen,
  GraduationCap,
  TrendingUp,
  Calendar,
  DollarSign,
  Award,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Bell,
  MessageSquare,
  BarChart3,
  PieChart as PieChartIcon,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { useNavigate } from "react-router-dom";
import useAuth from "../useAuth";

const Homepage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalCourses: 0,
    totalTests: 0,
    totalRevenue: 0,
    activeStudents: 0,
    pendingPayments: 0,
    upcomingTests: 0,
    todayAttendance: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching dashboard data
    // Replace with actual API calls
    setTimeout(() => {
      setStats({
        totalStudents: 1248,
        totalCourses: 24,
        totalTests: 156,
        totalRevenue: 452000,
        activeStudents: 1180,
        pendingPayments: 68,
        upcomingTests: 8,
        todayAttendance: 92,
      });
      setLoading(false);
    }, 1000);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  const quickActions = [
    {
      title: "Register Student",
      icon: Users,
      color: "from-blue-500 to-blue-600",
      path: "studentRegistration",
    },
    {
      title: "Create Test",
      icon: FileText,
      color: "from-purple-500 to-purple-600",
      path: "prepareNewTestSeries",
    },
    {
      title: "Add Question",
      icon: Award,
      color: "from-green-500 to-green-600",
      path: "addNewQuestion",
    },
    {
      title: "Mark Attendance",
      icon: CheckCircle,
      color: "from-orange-500 to-orange-600",
      path: "attendance",
    },
  ];

  const recentActivities = [
    {
      type: "Student Registration",
      description: "New student enrolled - Rahul Kumar",
      time: "5 min ago",
      icon: Users,
      color: "text-blue-500",
    },
    {
      type: "Test Completed",
      description: "Mock Test Series #12 completed by 45 students",
      time: "1 hour ago",
      icon: FileText,
      color: "text-purple-500",
    },
    {
      type: "Payment Received",
      description: "Fee payment of ₹15,000 received",
      time: "2 hours ago",
      icon: DollarSign,
      color: "text-green-500",
    },
    {
      type: "Announcement",
      description: "Holiday notice for next week posted",
      time: "3 hours ago",
      icon: Bell,
      color: "text-orange-500",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Welcome Header */}
        <motion.div variants={itemVariants} className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                Welcome back, {user?.name || "Admin"}! 👋
              </h1>
              <p className="text-gray-400 text-lg">
                Here's what's happening with your institute today
              </p>
            </div>
            <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg border border-gray-700">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300 text-sm">
                {new Date().toLocaleDateString("en-IN", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Key Stats Cards */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <StatsCard
            title="Total Students"
            value={stats.totalStudents}
            icon={Users}
            gradient="from-blue-500 to-cyan-500"
            trend={{ value: 12, isPositive: true }}
            subtitle={`${stats.activeStudents} active`}
          />
          <StatsCard
            title="Total Courses"
            value={stats.totalCourses}
            icon={BookOpen}
            gradient="from-purple-500 to-pink-500"
            trend={{ value: 3, isPositive: true }}
            subtitle="Across all batches"
          />
          <StatsCard
            title="Tests Conducted"
            value={stats.totalTests}
            icon={Award}
            gradient="from-green-500 to-emerald-500"
            trend={{ value: 8, isPositive: true }}
            subtitle={`${stats.upcomingTests} upcoming`}
          />
          <StatsCard
            title="Revenue (MTD)"
            value={`₹${(stats.totalRevenue / 1000).toFixed(0)}K`}
            icon={DollarSign}
            gradient="from-orange-500 to-red-500"
            trend={{ value: 15, isPositive: true }}
            subtitle={`${stats.pendingPayments} pending`}
          />
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={itemVariants}>
          <h2 className="text-2xl font-bold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <QuickActionCard
                key={index}
                {...action}
                onClick={() => navigate(action.path)}
              />
            ))}
          </div>
        </motion.div>

        {/* Charts and Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Performance Overview */}
          <motion.div
            variants={itemVariants}
            className="lg:col-span-2 bg-gray-800 rounded-2xl p-6 border border-gray-700"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Performance Overview
              </h2>
              <select className="bg-gray-900 text-gray-300 px-3 py-2 rounded-lg border border-gray-700 text-sm">
                <option>Last 7 Days</option>
                <option>Last 30 Days</option>
                <option>This Month</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <MetricCard
                label="Attendance Rate"
                value={`${stats.todayAttendance}%`}
                icon={CheckCircle}
                color="text-green-400"
              />
              <MetricCard
                label="Average Score"
                value="78.5%"
                icon={TrendingUp}
                color="text-blue-400"
              />
              <MetricCard
                label="Active Batches"
                value="12"
                icon={GraduationCap}
                color="text-purple-400"
              />
              <MetricCard
                label="Completion Rate"
                value="85%"
                icon={Activity}
                color="text-orange-400"
              />
            </div>

            {/* Simple Bar Chart Representation */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span>Students Enrolled</span>
                  <span>85%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 w-[85%]"></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span>Course Completion</span>
                  <span>72%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 w-[72%]"></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span>Test Performance</span>
                  <span>78%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 w-[78%]"></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span>Fee Collection</span>
                  <span>92%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 w-[92%]"></div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            variants={itemVariants}
            className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-400" />
              Recent Activity
            </h2>
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <ActivityItem key={index} {...activity} />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Upcoming Events & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Tests */}
          <motion.div
            variants={itemVariants}
            className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-400" />
              Upcoming Tests
            </h2>
            <div className="space-y-3">
              {[
                {
                  name: "Mathematics Final Test",
                  date: "Tomorrow",
                  students: 45,
                },
                { name: "Physics Mock Test", date: "Dec 15", students: 38 },
                { name: "Chemistry Unit Test", date: "Dec 18", students: 52 },
              ].map((test, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  <div>
                    <h3 className="text-white font-medium">{test.name}</h3>
                    <p className="text-gray-400 text-sm">
                      {test.students} students enrolled
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-purple-400 font-medium">
                      {test.date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Alerts & Notifications */}
          <motion.div
            variants={itemVariants}
            className="bg-gray-800 rounded-2xl p-6 border border-gray-700"
          >
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-400" />
              Alerts & Notifications
            </h2>
            <div className="space-y-3">
              <AlertItem
                type="warning"
                message="68 students have pending fee payments"
                action="View Details"
                onClick={() => navigate("payments")}
              />
              <AlertItem
                type="info"
                message="8 new student registrations pending approval"
                action="Review"
                onClick={() => navigate("studentsList")}
              />
              <AlertItem
                type="success"
                message="Monthly attendance report is ready"
                action="Download"
                onClick={() => navigate("attendanceReports")}
              />
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, gradient, trend, subtitle }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-gray-800 rounded-2xl p-6 border border-gray-700 relative overflow-hidden group"
    >
      <div
        className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-5 group-hover:opacity-10 transition-opacity`}
      ></div>

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 bg-gradient-to-br ${gradient} rounded-xl`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          {trend && (
            <div
              className={`flex items-center gap-1 ${
                trend.isPositive ? "text-green-400" : "text-red-400"
              }`}
            >
              {trend.isPositive ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">{trend.value}%</span>
            </div>
          )}
        </div>
        <h3 className="text-gray-400 text-sm mb-1">{title}</h3>
        <p className="text-3xl font-bold text-white mb-1">{value}</p>
        <p className="text-gray-500 text-xs">{subtitle}</p>
      </div>
    </motion.div>
  );
};

// Quick Action Card
const QuickActionCard = ({ title, icon: Icon, color, onClick }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="bg-gray-800 rounded-xl p-6 border border-gray-700 cursor-pointer group hover:border-gray-600 transition-all"
    >
      <div className={`p-3 bg-gradient-to-br ${color} rounded-lg w-fit mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-white font-semibold group-hover:text-blue-400 transition-colors">
        {title}
      </h3>
    </motion.div>
  );
};

// Metric Card
const MetricCard = ({ label, value, icon: Icon, color }) => {
  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
};

// Activity Item
const ActivityItem = ({ type, description, time, icon: Icon, color }) => {
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors">
      <div className={`p-2 rounded-lg bg-gray-800 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{type}</p>
        <p className="text-gray-400 text-xs truncate">{description}</p>
        <p className="text-gray-500 text-xs mt-1">{time}</p>
      </div>
    </div>
  );
};

// Alert Item
const AlertItem = ({ type, message, action, onClick }) => {
  const colors = {
    warning: "border-orange-500 bg-orange-500/10",
    info: "border-blue-500 bg-blue-500/10",
    success: "border-green-500 bg-green-500/10",
  };

  const iconColors = {
    warning: "text-orange-400",
    info: "text-blue-400",
    success: "text-green-400",
  };

  return (
    <div className={`border-l-4 ${colors[type]} p-4 rounded-r-lg`}>
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <AlertCircle
            className={`w-5 h-5 ${iconColors[type]} flex-shrink-0 mt-0.5`}
          />
          <p className="text-gray-300 text-sm">{message}</p>
        </div>
        {action && (
          <button
            onClick={onClick}
            className={`text-sm font-medium ${iconColors[type]} hover:underline ml-4`}
          >
            {action}
          </button>
        )}
      </div>
    </div>
  );
};

export default Homepage;
